/**
 * Mocked email previews shown when a visitor clicks a journey box on
 * /business/demo. Each entry mirrors the shape of a real email in the
 * platform (subject line + from/to header + branded HTML body) so a pub
 * owner can see exactly what their players will receive.
 *
 * Kept as static JSX-returning functions — nothing hits the backend.
 */
import { Trophy, MessageCircle } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";

const DEFAULT_BRAND = {
  pubName: "The Founders Arms",
  primary: "#0F766E",
  playerName: "Sarah",
  fromEmail: "hello@founders-arms.co.uk",
  toEmail: "sarah@example.com",
  logo: null,
};

// Slugify a pub name into a plausible mailbox local-part (spaces → dashes,
// non-alphanum stripped). Used to fabricate a from-address that matches
// the branded pub in the preview.
function pubToMailboxSlug(name) {
  return (name || "")
    .toLowerCase()
    .trim()
    .replace(/&/g, "and")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 40);
}

function resolveBrand(override) {
  if (!override) return DEFAULT_BRAND;
  const pubName = override.name || DEFAULT_BRAND.pubName;
  const localPart =
    pubToMailboxSlug(override.slug || pubName) || "hello";
  return {
    pubName,
    primary: override.primary || DEFAULT_BRAND.primary,
    playerName: DEFAULT_BRAND.playerName,
    fromEmail: `hello@${localPart}.co.uk`,
    toEmail: DEFAULT_BRAND.toEmail,
    logo: override.logo || null,
  };
}

// -------------------------------------------------------------------------
// Reusable "email client" chrome — subject + sender bar over any body.
// -------------------------------------------------------------------------
function EmailShell({ subject, from, to, children }) {
  return (
    <div className="rounded-sm border border-zinc-200 bg-white overflow-hidden">
      <div className="border-b border-zinc-200 bg-zinc-50 px-5 py-3">
        <p className="text-[15px] font-semibold text-zinc-950 leading-tight">
          {subject}
        </p>
        <p className="mt-1 text-[11px] text-zinc-500">
          <span className="font-semibold text-zinc-700">From:</span> {from}
          <span className="mx-2">·</span>
          <span className="font-semibold text-zinc-700">To:</span> {to}
        </p>
      </div>
      <div className="p-6 text-[13px] text-zinc-800 leading-relaxed">
        {children}
      </div>
    </div>
  );
}

function EmailFooter({ brand }) {
  return (
    <p className="mt-6 pt-4 border-t border-zinc-200 text-[10px] uppercase tracking-widest text-zinc-400">
      {brand.pubName} · Football Challenge · Unsubscribe · Privacy
    </p>
  );
}

// -------------------------------------------------------------------------
// Individual email bodies. Kept intentionally simple + brand-consistent.
// -------------------------------------------------------------------------
const PREVIEWS = {
  "journey-01-welcome": {
    label: "Welcome email",
    render: (brand) => (
      <EmailShell
        subject={`Welcome to ${brand.pubName} Football Challenge, ${brand.playerName}!`}
        from={brand.fromEmail}
        to={brand.toEmail}
      >
        <h2 className="font-heading text-2xl font-bold text-zinc-950 leading-tight">
          You&apos;re in, {brand.playerName}.
        </h2>
        <p className="mt-3">
          Thanks for joining the {brand.pubName} Football Challenge — 6
          predictions on 6 matches, every week, with real prizes to win at
          the bar.
        </p>
        <p className="mt-3">
          Your first prediction card is already live. Lock in your picks
          before kickoff and you&apos;re in the running.
        </p>
        <a
          href="#"
          className="inline-block mt-5 rounded-sm px-4 py-2.5 text-[12px] font-bold uppercase tracking-widest text-white"
          style={{ backgroundColor: brand.primary }}
        >
          Make my predictions →
        </a>
        <EmailFooter brand={brand} />
      </EmailShell>
    ),
  },

  "journey-02-kickoff": {
    label: "Kickoff reminder",
    render: (brand) => (
      <EmailShell
        subject="Kickoff in ~2h: England vs Argentina"
        from={brand.fromEmail}
        to={brand.toEmail}
      >
        <p className="text-[11px] font-bold uppercase tracking-widest text-zinc-500">
          Reminder
        </p>
        <h2 className="mt-1 font-heading text-2xl font-bold text-zinc-950 leading-tight">
          Kickoff in ~2 hours.
        </h2>
        <p className="mt-3">
          Hi {brand.playerName} — England vs Argentina kicks off at 20:00
          tonight and you haven&apos;t locked in your prediction yet.
        </p>
        <p className="mt-3">
          Once the whistle blows, predictions close. Get in before then to
          keep your streak alive.
        </p>
        <a
          href="#"
          className="inline-block mt-5 rounded-sm px-4 py-2.5 text-[12px] font-bold uppercase tracking-widest text-white"
          style={{ backgroundColor: brand.primary }}
        >
          Lock in my prediction →
        </a>
        <EmailFooter brand={brand} />
      </EmailShell>
    ),
  },

  "journey-03-daily": {
    label: "Daily league update",
    render: (brand) => (
      <EmailShell
        subject={`Daily League Update — you finished #3 on Sun 8 Jun`}
        from={brand.fromEmail}
        to={brand.toEmail}
      >
        <p className="text-[11px] font-bold uppercase tracking-widest text-zinc-500">
          Match-day recap
        </p>
        <h2 className="mt-1 font-heading text-2xl font-bold text-zinc-950 leading-tight">
          You scored 9 pts today.
        </h2>
        <p className="mt-3">
          Nice one, {brand.playerName}. You climbed{" "}
          <span className="font-bold text-emerald-700">▲ 2 spots</span> —
          you&apos;re now #3 on the {brand.pubName} league.
        </p>
        <div className="mt-4 rounded-sm border border-zinc-200">
          <div className="border-b border-zinc-200 bg-zinc-50 px-4 py-2">
            <p className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">
              Top 5 at {brand.pubName}
            </p>
          </div>
          {[
            { rank: 1, name: "Tom H.", pts: 42 },
            { rank: 2, name: "Emma K.", pts: 38 },
            { rank: 3, name: `${brand.playerName} (you)`, pts: 36, mine: true },
            { rank: 4, name: "Ben R.", pts: 31 },
            { rank: 5, name: "Alex M.", pts: 28 },
          ].map((r) => (
            <div
              key={r.rank}
              className={`flex items-center justify-between px-4 py-2 border-b border-zinc-100 last:border-b-0 ${
                r.mine ? "bg-emerald-50" : ""
              }`}
            >
              <span className="text-[13px] font-semibold text-zinc-800">
                {r.rank}. {r.name}
              </span>
              <span className="text-[13px] font-bold tabular-nums text-zinc-950">
                {r.pts}
              </span>
            </div>
          ))}
        </div>
        <EmailFooter brand={brand} />
      </EmailShell>
    ),
  },

  "journey-04-mini-leagues": {
    label: "Mini-league invites",
    render: (brand) => (
      <EmailShell
        subject="You've been invited to 'The Sunday Crew' mini-league"
        from={brand.fromEmail}
        to={brand.toEmail}
      >
        <p className="text-[11px] font-bold uppercase tracking-widest text-zinc-500">
          Mini-league invite
        </p>
        <h2 className="mt-1 font-heading text-2xl font-bold text-zinc-950 leading-tight">
          Ben invited you to a mini-league.
        </h2>
        <p className="mt-3">
          Ben R. has started <strong>&ldquo;The Sunday Crew&rdquo;</strong>{" "}
          — a private mini-league inside {brand.pubName}. 4 players so far,
          bragging rights on the line.
        </p>
        <p className="mt-3 text-zinc-500 text-[12px]">
          Mini-leagues let you compete against a smaller group — mates,
          workmates, family — on top of the main pub league.
        </p>
        <a
          href="#"
          className="inline-block mt-5 rounded-sm px-4 py-2.5 text-[12px] font-bold uppercase tracking-widest text-white"
          style={{ backgroundColor: brand.primary }}
        >
          Join The Sunday Crew →
        </a>
        <EmailFooter brand={brand} />
      </EmailShell>
    ),
  },

  "journey-05-whatsapp": {
    label: "Community WhatsApp",
    render: (brand) => (
      <EmailShell
        subject={`${brand.pubName} — join the WhatsApp banter group`}
        from={brand.fromEmail}
        to={brand.toEmail}
      >
        <p className="text-[11px] font-bold uppercase tracking-widest text-zinc-500">
          Community
        </p>
        <h2 className="mt-1 font-heading text-2xl font-bold text-zinc-950 leading-tight">
          Join the {brand.pubName} WhatsApp.
        </h2>
        <p className="mt-3">
          Predictions are more fun with banter. Join {brand.pubName}&apos;s
          private WhatsApp group for match talk, screenshots of your latest
          picks, and a bit of wind-up when someone gets it wrong.
        </p>
        <a
          href="#"
          className="inline-flex items-center gap-2 mt-5 rounded-sm px-4 py-2.5 text-[12px] font-bold uppercase tracking-widest text-white bg-[#25D366]"
        >
          <MessageCircle className="h-3.5 w-3.5" />
          Join on WhatsApp →
        </a>
        <p className="mt-4 text-[11px] text-zinc-500">
          Optional. You can keep playing without joining — the group is
          just for the community.
        </p>
        <EmailFooter brand={brand} />
      </EmailShell>
    ),
  },

  "journey-06-final-standings": {
    label: "Final standings email",
    render: (brand) => (
      <EmailShell
        subject={`Season closed — you finished #4 at ${brand.pubName}`}
        from={brand.fromEmail}
        to={brand.toEmail}
      >
        <p className="text-[11px] font-bold uppercase tracking-widest text-zinc-500">
          End of season
        </p>
        <h2 className="mt-1 font-heading text-2xl font-bold text-zinc-950 leading-tight">
          The final table is in.
        </h2>
        <p className="mt-3">
          Thanks for playing, {brand.playerName}. You finished{" "}
          <span className="font-bold" style={{ color: brand.primary }}>
            #4
          </span>{" "}
          on {brand.pubName} with 214 points across 24 match-days.
        </p>
        <div className="mt-4 rounded-sm border border-zinc-200">
          <div
            className="px-4 py-2.5 text-white"
            style={{ backgroundColor: brand.primary }}
          >
            <p className="text-[10px] font-bold uppercase tracking-widest">
              Final league table · {brand.pubName}
            </p>
          </div>
          {[
            { rank: 1, name: "Tom H.", pts: 268, badge: "🏆" },
            { rank: 2, name: "Emma K.", pts: 251 },
            { rank: 3, name: "Ben R.", pts: 229 },
            { rank: 4, name: `${brand.playerName} (you)`, pts: 214, mine: true },
            { rank: 5, name: "Alex M.", pts: 198 },
          ].map((r) => (
            <div
              key={r.rank}
              className={`flex items-center justify-between px-4 py-2 border-b border-zinc-100 last:border-b-0 ${
                r.mine ? "bg-emerald-50" : ""
              }`}
            >
              <span className="text-[13px] font-semibold text-zinc-800">
                {r.rank}. {r.name} {r.badge || ""}
              </span>
              <span className="text-[13px] font-bold tabular-nums text-zinc-950">
                {r.pts}
              </span>
            </div>
          ))}
        </div>
        <p className="mt-4 text-[12px] text-zinc-500">
          Season champion is Tom H. — congratulations! New season starts
          Monday. See you then.
        </p>
        <EmailFooter brand={brand} />
      </EmailShell>
    ),
  },

  "journey-07-champion": {
    label: "Season champion",
    render: (brand) => (
      <EmailShell
        subject="Season Champion — you've won £250"
        from={brand.fromEmail}
        to={brand.toEmail}
      >
        <div
          className="rounded-sm p-5 text-white"
          style={{ backgroundColor: brand.primary }}
        >
          <Trophy className="h-6 w-6 mb-3" />
          <p className="text-[10px] font-bold uppercase tracking-widest text-white/80">
            Season Champion
          </p>
          <h2 className="mt-1 font-heading text-3xl font-bold leading-tight">
            You did it, Tom.
          </h2>
          <p className="mt-3 text-white/90 text-[13px]">
            268 points across 24 match-days. Top of {brand.pubName}. Season
            Champion, 2026/27.
          </p>
        </div>
        <p className="mt-5">
          Your prize —{" "}
          <span className="font-bold" style={{ color: brand.primary }}>
            £250 bar credit
          </span>{" "}
          — is ready to claim at the bar. We&apos;ve just sent a separate
          voucher email with a scan-code to redeem.
        </p>
        <a
          href="#"
          className="inline-block mt-5 rounded-sm px-4 py-2.5 text-[12px] font-bold uppercase tracking-widest text-white"
          style={{ backgroundColor: brand.primary }}
        >
          View my voucher →
        </a>
        <EmailFooter brand={brand} />
      </EmailShell>
    ),
  },

  "journey-08-voucher": {
    label: "Prize & voucher claim",
    render: (brand) => (
      <EmailShell
        subject={`Your ${brand.pubName} voucher — £250 bar credit`}
        from={brand.fromEmail}
        to={brand.toEmail}
      >
        <p className="text-[11px] font-bold uppercase tracking-widest text-zinc-500">
          Voucher
        </p>
        <h2 className="mt-1 font-heading text-2xl font-bold text-zinc-950 leading-tight">
          £250 bar credit — ready to redeem.
        </h2>
        <p className="mt-3">
          Congratulations Tom! Show this voucher to any staff member at{" "}
          {brand.pubName} — they&apos;ll scan the code and apply the credit
          to your tab.
        </p>
        <div className="mt-5 rounded-sm border-2 border-dashed border-zinc-300 p-6 text-center bg-zinc-50">
          <p className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">
            {brand.pubName} · Bar credit
          </p>
          <p
            className="mt-2 font-heading text-5xl font-bold"
            style={{ color: brand.primary }}
          >
            £250
          </p>
          <div className="mt-4 inline-block bg-white border border-zinc-200 rounded-sm px-4 py-2 font-mono text-[13px] tracking-widest text-zinc-800">
            FA-CHAMP-2627-TH
          </div>
          <p className="mt-3 text-[10px] uppercase tracking-widest text-zinc-500">
            Expires 30 days from issue
          </p>
        </div>
        <p className="mt-4 text-[11px] text-zinc-500">
          Reminder emails will follow 7, 3, and 1 day before expiry.
        </p>
        <EmailFooter brand={brand} />
      </EmailShell>
    ),
  },
};

export default function EmailPreviewDialog({ openId, onClose, brand: brandOverride }) {
  const preview = openId ? PREVIEWS[openId] : null;
  const brand = resolveBrand(brandOverride);
  return (
    <Dialog open={!!openId} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        data-testid="demo-email-preview-dialog"
        className="max-w-2xl max-h-[90vh] overflow-y-auto p-0 gap-0 bg-white"
      >
        <VisuallyHidden>
          <DialogTitle>
            {preview ? `Email preview — ${preview.label}` : "Email preview"}
          </DialogTitle>
          <DialogDescription>
            A mocked example of what this email looks like when it lands in a
            player&apos;s inbox.
          </DialogDescription>
        </VisuallyHidden>
        <div className="bg-zinc-100 border-b border-zinc-200 px-6 py-3 flex items-center gap-3">
          {brand.logo ? (
            <img
              src={brand.logo}
              alt={`${brand.pubName} logo`}
              data-testid="demo-email-preview-logo"
              className="h-9 w-9 rounded-sm object-contain bg-white border border-zinc-200"
            />
          ) : null}
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-zinc-500">
              Sample email · {brand.pubName}
            </p>
            <p className="text-[15px] font-heading font-bold text-zinc-950">
              {preview?.label || ""}
            </p>
          </div>
        </div>
        <div className="p-6 bg-zinc-50">
          {preview ? preview.render(brand) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
}
